import React from 'react';
import { cn } from '@/lib/utils';

type TooltipVariant = 'dark' | 'light';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  disabled?: boolean;
  side?: 'right' | 'top' | 'bottom' | 'left';
  /**
   * Colour scheme. `dark` (default) is the original high-contrast chip.
   * `light` is a calm, readable popover-style box (light background, dark text,
   * border + shadow) for surfaces where the dark chip looks heavy.
   */
  variant?: TooltipVariant;
  /**
   * Widen the box from `max-w-xs` to `max-w-sm` for longer, multi-paragraph
   * content (e.g. a "how it's calculated" explanation). Default `false`.
   */
  wide?: boolean;
  className?: string;
}

const variantClasses: Record<TooltipVariant, string> = {
  dark: 'bg-foreground text-background',
  light: 'border border-border bg-popover text-popover-foreground shadow-md',
};

export function Tooltip({
  content,
  children,
  disabled = false,
  side = 'right',
  variant = 'dark',
  wide = false,
  className,
}: Readonly<TooltipProps>) {
  if (disabled) {
    return <>{children}</>;
  }

  const positionClasses = {
    right: 'left-full ml-2 top-1/2 -translate-y-1/2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    top: 'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
  };

  return (
    <div className={cn('group relative', className)}>
      {children}
      <div
        role="tooltip"
        className={cn(
          // `w-max` sizes the box to its content (capped by max-w-*) so it
          // never shrink-wraps against a narrow trigger and truncates the text.
          // `whitespace-pre-line` preserves `\n` paragraph/step breaks in
          // multi-line content while still wrapping on width.
          'pointer-events-none absolute z-50 w-max whitespace-pre-line',
          wide ? 'max-w-sm' : 'max-w-xs',
          'rounded-md px-2 py-1 text-xs',
          'opacity-0 transition-opacity duration-200',
          // Open on hover and on keyboard focus of the trigger (a11y).
          'group-hover:opacity-100 group-focus-within:opacity-100',
          variantClasses[variant],
          positionClasses[side]
        )}
      >
        {content}
      </div>
    </div>
  );
}

